import { useLayoutManagementStore } from "@hooks/stores";
import { Stack } from "@ucchub/ui";
import Accordion from "@vfuk/core-accordion";
import useAccordion from "@vfuk/core-accordion/dist/hooks/useAccordion";
import {
  CollapsibleContainer,
  CollapsibleContainerBody,
  CollapsibleContainerHeader,
} from "@vfuk/core-collapsible-container";
import Link from "@vfuk/core-link";
import Paragraph from "@vfuk/core-paragraph";
import Spacing from "@vfuk/core-spacing";
import { ReactNode, useEffect } from "react";
import {
  ProductChildStyles,
  ProductNavStyles,
  ActiveParagraph,
  ActiveLink,
} from "./ProductsNavStyles";
import { useLocation } from "react-router-dom";

const ProductsNav = ({ children }: { children: ReactNode }) => {
  const { navbar, showProductLinks } = useLayoutManagementStore(
    (state) => state
  );
  const { primaryNav } = navbar;
  const { productLinks } = primaryNav;

  const { pathname } = useLocation();
  const { openIds, onToggle } = useAccordion({ multi: true });

  // Only show the Products Nav if navigated on a Product
  const isOnProductURL = () => {
    const productURLs = productLinks?.map((link) => link.url);
    return productURLs?.some((el) => pathname.includes(el));
  };

  // The final variable to control the visibility of the Products Nav
  const isShowNav =
    isOnProductURL() && productLinks !== undefined && showProductLinks;

  useEffect(() => {
    // Check whether each link's URL is in the current URL, then add that URL to the array
    const getActiveLinks = () => {
      const activeLink = productLinks?.find((link) =>
        pathname.includes(link.url)
      )?.url;

      // If the current URL is already opened, don't try to open again
      if (!openIds.includes(activeLink || "")) onToggle(activeLink || "");
    };

    getActiveLinks();
  }, [onToggle, openIds, pathname, productLinks]);

  return (
    <div>
      {isShowNav && (
        <ProductNavStyles>
          <Accordion
            multi
            onToggle={onToggle}
            appearance="primary"
            level={2}
            openIds={openIds}
          >
            {productLinks.map((product) => (
              <CollapsibleContainer key={product.text} id={product.url}>
                <CollapsibleContainerHeader>
                  <Stack>
                    <Spacing spacingLevel={{ right: 2 }}>
                      <img
                        width="32px"
                        height="32px"
                        alt={`${product.text}'s Icon`}
                        src={`/core/assets/ws10/icons/SourceMidRenderLightIcons/${product.icon}.svg`}
                      />
                    </Spacing>

                    <Paragraph>{product.text}</Paragraph>
                  </Stack>
                </CollapsibleContainerHeader>

                <Spacing spacingLevel={{ bottom: 4 }}>
                  <CollapsibleContainerBody>
                    {product.links.map((section) => (
                      <div key={section.text}>
                        <Spacing spacingLevel={{ top: 4, bottom: 2 }}>
                          {section.url ? (
                            <Link customRouterProps={{ to: section.url }}>
                              <ActiveParagraph
                                isActive={pathname === section.url}
                              >
                                <Paragraph weight={3}>{section.text}</Paragraph>
                              </ActiveParagraph>
                            </Link>
                          ) : (
                            <Paragraph weight={3}>{section.text}</Paragraph>
                          )}
                        </Spacing>

                        <Stack direction="column" align="flex-start">
                          {section.links.map((link) => (
                            <Spacing
                              key={link.text}
                              spacingLevel={{ bottom: 1 }}
                            >
                              <ActiveLink isActive={pathname === link.url}>
                                {link.text === "Incident management" && (
                                  <Link
                                    onClick={() =>
                                      window.open(link.url, "_blank")
                                    }
                                  >
                                    {link.text}
                                  </Link>
                                )}
                                {link.text !== "Incident management" && (
                                  <Link
                                    customRouterProps={{
                                      to: link.url,
                                    }}
                                  >
                                    {link.text}
                                  </Link>
                                )}
                              </ActiveLink>
                            </Spacing>
                          ))}
                        </Stack>
                      </div>
                    ))}
                  </CollapsibleContainerBody>
                </Spacing>
              </CollapsibleContainer>
            ))}
          </Accordion>
        </ProductNavStyles>
      )}
      <ProductChildStyles noMargin={!isShowNav}>{children}</ProductChildStyles>
    </div>
  );
};

export default ProductsNav;
